import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {useAuth} from 'services/authentication/useAuth';
import {hasLoginV2Workflow, requiresHcpVerification, shouldShowNpiModalRejectButton} from 'services/authentication';
import {HcpVerification, NpiLoginModal} from './Modal';
import {RedirectModal} from 'components/Modals';

/**
 * LoginModals Component
 * Renders authentication modals as overlays on top of the page
 * Used to show HCP verification and NPI login modals on coverage page
 *
 * @param {Object} props
 * @param {Object} props.styleSheet - Aphrodite styleSheet object
 */
const LoginModals = ({styleSheet}) => {
  const authenticated = useSelector((state) => state.login.get('authenticated'));
  const settings = useSelector((state) => state.settings.get('settings').toJS());
  const [showRedirectModal, setShowRedirectModal] = useState(false);


  // Always call useAuth (React hooks rule), but check authenticated state first in the hook
  const {
    showHcpVerification,
    isNpiModalType,
    shouldShowLoginInModal,
    handleHcpVerificationAccept,
    handleHcpVerificationReject,
    handleHcpVerificationCancel
  } = useAuth();

  // Safety check: return null when loginV2 is off. Legacy sites use LoginModal from
  // components/Modals (src/components/Modals/index.jsx), mounted in PalPage when
  // !hasLoginV2Workflow (src/pages/Pal/index.jsx).
  if (!hasLoginV2Workflow(settings)) {
    return null;
  }

  // Don't show modals if already authenticated
  if (authenticated) {
    return null;
  }

  const handleHcpReject = () => {
    handleHcpVerificationReject();
    setShowRedirectModal(true);
  };

  const handleRedirectModalExit = () => {
    setShowRedirectModal(false);
    window.location.assign(settings.page.redirectLink);
  };

  const handleNpiModalReject = () => {
    setShowRedirectModal(true);
  };

  // Show redirect modal if user rejected HCP verification
  if (showRedirectModal) {
    return (
      <RedirectModal
        settings={settings}
        styleSheet={styleSheet}
        showRedirectModal={showRedirectModal}
        handleExit={handleRedirectModalExit}
      />
    );
  }

  // Show HCP verification modal first if needed
  if (showHcpVerification && !authenticated) {
    return (
      <HcpVerification
        onAccept={handleHcpVerificationAccept}
        onReject={handleHcpReject}
        styleSheet={styleSheet}
        show={showHcpVerification}
      />
    );
  }

  // Show NPI login modal if:
  // 1. After HCP verification (hcpVerified === true) AND type is npiModal
  // 2. HCP verification disabled AND authentication required AND type is npiModal
  if (shouldShowLoginInModal && isNpiModalType && !authenticated) {
    // Only show cancel button if HCP verification is enabled (so user can go back to HCP modal)
    const showCancelButton = requiresHcpVerification(settings);
    // Show reject button if HCP verification is disabled (triggers redirect modal)
    const showRejectButton = shouldShowNpiModalRejectButton(settings);
    
    return (
      <NpiLoginModal
        styleSheet={styleSheet}
        show={true}
        onSuccess={() => {
          // Modal will disappear when authenticated becomes true
        }}
        onCancel={showCancelButton ? handleHcpVerificationCancel : null}
        onReject={showRejectButton ? handleNpiModalReject : null}
      />
    );
  }

  // No modals to show
  return null;
};

LoginModals.propTypes = {
  styleSheet: PropTypes.object.isRequired
};

export default LoginModals;
